"use client";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useFeatureNavigation } from "../../navigation/use-feature-navigation";
import {
  clampQuery,
  isSearchActivationKey,
  type SiteSearchCoworldContext,
} from "./search-input";
import {
  searchHrefOf,
  returnHrefOf,
  querySearchHref,
} from "./search-addresses";
import { synchronizeSearchDraft, type SearchDraft } from "./search-draft";

export function useSearchRow({
  coworld,
  onNavigate,
}: {
  readonly coworld: SiteSearchCoworldContext | undefined;
  readonly onNavigate: (() => void) | undefined;
}) {
  const { pathname, search, push, replace } = useFeatureNavigation();
  const searchParams = new URLSearchParams(search);
  const searchParamsString = searchParams.toString();
  const searchHref = searchHrefOf({ coworld });
  const onSearchRoute = pathname === searchHref;
  const urlQuery = onSearchRoute ? clampQuery(searchParams.get("q") ?? "") : "";
  const route = JSON.stringify([pathname, searchHref]);
  const [draft, setDraft] = useState<SearchDraft>({
    route,
    search: searchParamsString,
    active: onSearchRoute,
    query: urlQuery,
    issued: [],
    pending: null,
  });

  if (draft.route !== route || draft.search !== searchParamsString) {
    setDraft(
      synchronizeSearchDraft({
        draft,
        route,
        search: searchParamsString,
        onSearchRoute,
        currentHref: searchHrefOf({ coworld, search: searchParams }),
        urlQuery,
      }),
    );
  }

  const inputRef = useRef<HTMLInputElement | null>(null);
  const pending = draft.pending;
  useEffect(() => {
    if (!pending) return;

    const timer = window.setTimeout(() => {
      setDraft((previous) => ({
        ...previous,
        issued: [...previous.issued, pending.href],
        pending: null,
      }));
      replace({ href: pending.href, scroll: false });
    }, 200);
    return () => window.clearTimeout(timer);
  }, [pending, replace]);
  const restoreHistory = useEffectEvent(() => {
    setDraft((previous) => ({
      ...previous,
      query: clampQuery(
        new URLSearchParams(window.location.search).get("q") ?? "",
      ),
      issued: [],
      pending: null,
    }));
  });
  useEffect(() => {
    const onPopState = () => restoreHistory();
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
  useEffect(() => {
    if (draft.active && onSearchRoute) inputRef.current?.focus();
  }, [draft.active, onSearchRoute]);
  const activationParams = new URLSearchParams();
  const from = onSearchRoute ? searchParams.get("from") : pathname;

  if (from) activationParams.set("from", from);

  const activationHref = searchHrefOf({ coworld, search: activationParams });
  const activate = () => {
    onNavigate?.();
    setDraft((previous) => ({
      ...previous,
      active: true,
      issued: [...previous.issued, activationHref],
    }));
  };
  const activateFromKeyboard = useEffectEvent((event: KeyboardEvent) => {
    if (!isSearchActivationKey(event)) return;

    event.preventDefault();

    if (onSearchRoute) {
      inputRef.current?.focus();
      return;
    }

    activate();
    push({ href: activationHref });
  });
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => activateFromKeyboard(event);
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);
  const changeQuery = (value: string) => {
    setDraft((previous) => ({
      ...previous,
      query: value,
      pending: {
        href: querySearchHref({
          coworld,
          search: onSearchRoute ? searchParams : activationParams,
          query: value,
        }),
      },
    }));
  };
  const leave = () => {
    setDraft((previous) => ({
      ...previous,
      active: false,
      issued: [],
      pending: null,
    }));
    onNavigate?.();
    push({ href: returnHrefOf({ from: searchParams.get("from"), coworld }) });
  };

  return {
    draft,
    inputRef,
    activationHref,
    activate,
    changeQuery,
    leave,
  };
}
